import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Mail, MailOpen, Phone, Trash2, Reply, Check, AlertCircle, Clock } from "lucide-react";
import { format } from "date-fns";

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone?: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

export const MessagesManager = () => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from("contact_messages")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setMessages(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleRead = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from("contact_messages")
        .update({ is_read: !currentStatus })
        .eq("id", id);

      if (error) throw error;

      setMessages(messages.map(msg =>
        msg.id === id ? { ...msg, is_read: !currentStatus } : msg
      ));

      toast({
        title: "Success",
        description: `Message marked as ${!currentStatus ? "read" : "unread"}`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteMessage = async (id: string) => {
    if (!confirm("Are you sure you want to delete this message?")) return;

    try {
      const { error } = await supabase
        .from("contact_messages")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setMessages(messages.filter(msg => msg.id !== id));

      toast({
        title: "Success",
        description: "Message deleted successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const unreadMessages = messages.filter(m => !m.is_read);
  const readMessages = messages.filter(m => m.is_read);

  const renderMessageCard = (message: ContactMessage) => (
    <Card key={message.id} className="overflow-hidden border-l-4 shadow-sm hover:shadow-md transition-shadow"
      style={{ borderLeftColor: message.is_read ? '#9ca3af' : '#3b82f6' }}
    >
      <CardHeader className="bg-muted/30 pb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div className="space-y-1">
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              {message.is_read ? <MailOpen className="h-4 w-4 text-muted-foreground" /> : <Mail className="h-4 w-4 text-primary" />}
              {message.name}
            </CardTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="h-3 w-3" /> <a href={`mailto:${message.email}`} className="hover:text-primary">{message.email}</a>
              {message.phone && (
                <>
                  <span className="text-muted-foreground/50">|</span>
                  <Phone className="h-3 w-3" /> <a href={`tel:${message.phone}`} className="hover:text-primary">{message.phone}</a>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground bg-background border px-2 py-1 rounded">
            <Clock className="h-3 w-3" />
            {format(new Date(message.created_at), "MMM dd, yyyy HH:mm")}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="bg-muted/30 p-4 rounded-md text-sm whitespace-pre-wrap leading-relaxed">
          {message.message}
        </div>
      </CardContent>
      <CardFooter className="bg-muted/10 border-t p-3 flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={() => window.open(`mailto:${message.email}?subject=Re: Inquiry from ${message.name}`)} className="h-8 gap-1">
          <Reply className="h-3.5 w-3.5" /> Reply
        </Button>
        <Button size="sm" variant={message.is_read ? "outline" : "secondary"} onClick={() => toggleRead(message.id, message.is_read)} className="h-8 gap-1">
          {message.is_read ? (
            <>Mark Unread</>
          ) : (
            <><Check className="h-3.5 w-3.5" /> Mark Read</>
          )}
        </Button>
        {hasPermission('delete_messages') && (
          <Button size="sm" variant="destructive" onClick={() => deleteMessage(message.id)} className="h-8 gap-1">
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </Button>
        )}
      </CardFooter>
    </Card>
  );

  const renderEmptyState = (type: string) => (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
          <AlertCircle className="h-6 w-6" />
        </div>
        <h3 className="text-lg font-semibold mb-1">No {type} messages</h3>
        <p className="text-sm">You're all caught up!</p>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Messages</h2>
          <p className="text-muted-foreground mt-1">View and manage customer inquiries.</p>
        </div>
        <div className="flex items-center gap-2 bg-muted p-1 rounded-lg text-sm font-medium">
          <span className="px-3 py-1 bg-background shadow-sm rounded-md">{messages.length} Total</span>
          <span className="px-3 py-1 text-primary">{unreadMessages.length} Unread</span>
        </div>
      </div>

      <Tabs defaultValue="unread" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="unread">Unread</TabsTrigger>
          <TabsTrigger value="read">Read</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>

        <TabsContent value="unread" className="space-y-4 mt-6">
          {unreadMessages.length > 0 ? unreadMessages.map(renderMessageCard) : renderEmptyState('unread')}
        </TabsContent>

        <TabsContent value="read" className="space-y-4 mt-6">
          {readMessages.length > 0 ? readMessages.map(renderMessageCard) : renderEmptyState('read')}
        </TabsContent>

        <TabsContent value="all" className="space-y-4 mt-6">
          {messages.length > 0 ? messages.map(renderMessageCard) : renderEmptyState('any')}
        </TabsContent>
      </Tabs>
    </div>
  );
};
